import { useState, type FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { navLinks } from "@/lib/nav";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    setMenuOpen(false);
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    [
      "relative px-3 py-2 text-sm rounded-md transition-colors duration-200 whitespace-nowrap",
      isActive
        ? "text-primary-700 font-semibold"
        : "text-foreground-600 hover:text-primary-700 hover:bg-background-100",
    ].join(" ");

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2.5 shrink-0" aria-label="乱伦专题首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-background-50">
            <i className="ri-scales-3-line text-lg" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading text-lg font-bold text-foreground-950">乱伦专题</span>
            <span className="hidden text-[11px] tracking-wide text-foreground-500 sm:block">法律与伦理知识门户</span>
          </span>
        </Link>

        <nav className="ml-2 hidden flex-1 items-center gap-1 lg:flex">
          {navLinks.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.to === "/"} className={linkClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={handleSearch}
          className="ml-auto hidden w-64 items-center gap-2 rounded-full border border-background-300 bg-background-100 px-3 py-1.5 focus-within:border-primary-400 md:flex"
          role="search"
        >
          <i className="ri-search-line text-base text-foreground-400" />
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="搜索法规、伦理、案例"
            aria-label="站内搜索"
            className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
          />
        </form>

        <Link
          to="/search"
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-600 transition-colors hover:bg-background-100 md:hidden"
          aria-label="搜索"
        >
          <i className="ri-search-line text-lg" />
        </Link>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-100 lg:hidden"
          aria-label="打开菜单"
          aria-expanded={menuOpen}
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="animate-slideDown border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 lg:hidden">
          <form
            onSubmit={handleSearch}
            className="mb-2 flex items-center gap-2 rounded-lg border border-background-300 bg-background-100 px-3 py-2"
            role="search"
          >
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索法规、伦理、案例"
              aria-label="站内搜索"
              className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
            />
          </form>
          <div className="flex flex-col">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors",
                    isActive
                      ? "bg-primary-50 text-primary-700 font-semibold"
                      : "text-foreground-700 hover:bg-background-100",
                  ].join(" ")
                }
              >
                <i className={`${link.icon} text-lg`} />
                {link.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}